/**
 * BigCommerce Integration
 * Provides integration with BigCommerce e-commerce platform using REST API and webhooks
 */

import { BaseEcommerceIntegration } from '../base/BaseEcommerceIntegration';
import {
  EcommerceProduct,
  EcommerceCustomer,
  EcommerceOrder,
  RecommendationContext,
  ProductImage,
  ProductVariant,
  CustomerAddress,
  OrderLineItem
} from '../base/BaseEcommerceIntegration';
import { IntegrationConfig, ConnectionResult, TestResult, ValidationResult } from '../../../types/integration';

// BigCommerce-specific types
export interface BigCommerceConfig {
  storeHash: string;
  accessToken: string;
  clientId?: string;
  clientSecret?: string;
  apiVersion: 'v2' | 'v3';
  webhookSecret?: string;
  enableWebhooks: boolean;
}

export interface BigCommerceProduct {
  id: number;
  name: string;
  type: 'physical' | 'digital';
  sku?: string;
  description?: string;
  price: number;
  retail_price?: number;
  inventory_level?: number;
  is_visible?: boolean;
  categories: number[];
  search_keywords?: string;
  date_created?: string;
  date_modified?: string;
  images?: BigCommerceProductImage[];
  variants?: BigCommerceProductVariant[];
}

export interface BigCommerceProductImage {
  id?: number;
  image_url?: string;
  url_standard?: string;
  description?: string;
  sort_order?: number;
}

export interface BigCommerceProductVariant {
  id?: number;
  price?: number;
  sku?: string;
  inventory_level?: number;
  option_values?: BigCommerceOptionValue[];
}

export interface BigCommerceOptionValue {
  id: number;
  label: string;
  option_display_name: string;
}

export interface BigCommerceCustomer {
  id?: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  company?: string;
  date_created?: string;
  date_modified?: string;
  store_credit?: number;
  customer_group_id?: number;
  notes?: string;
  addresses?: BigCommerceAddress[];
}

export interface BigCommerceAddress {
  id?: number;
  first_name?: string;
  last_name?: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  state_or_province: string;
  postal_code: string;
  country_code: string;
  phone?: string;
}

export interface BigCommerceOrder {
  id?: number;
  customer_id?: number;
  date_created?: string;
  date_modified?: string;
  status_id: number;
  status?: string;
  total_inc_tax: number;
  subtotal_inc_tax: number;
  total_tax: number;
  shipping_cost_inc_tax: number;
  currency_code: string;
  billing_address?: BigCommerceBillingAddress;
  shipping_addresses?: BigCommerceShippingAddress[];
  products?: BigCommerceOrderProduct[];
  payment_method: string;
  payment_status?: string;
  order_is_digital: boolean;
  channel_id?: number;
  order_source?: string;
}

export interface BigCommerceBillingAddress {
  first_name?: string;
  last_name?: string;
  company?: string;
  street_1?: string;
  street_2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country_iso2?: string;
  phone?: string;
  email?: string;
}

export interface BigCommerceShippingAddress {
  id?: number;
  first_name?: string;
  last_name?: string;
  company?: string;
  street_1?: string;
  street_2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country_iso2?: string;
  phone?: string;
}

export interface BigCommerceOrderProduct {
  id?: number;
  product_id: number;
  name: string;
  sku?: string;
  quantity: number;
  price_inc_tax: number;
  base_price: number;
  total_ex_tax: number;
  total_inc_tax: number;
}

export class BigCommerceIntegration extends BaseEcommerceIntegration {
  private platformConfig: BigCommerceConfig;
  private baseUrl: string;
  private headers: Record<string, string>;

  constructor(integrationConfig: IntegrationConfig) {
    super(integrationConfig);
    this.platformConfig = integrationConfig.settings.customSettings as BigCommerceConfig;
    this.baseUrl = `https://api.bigcommerce.com/stores/${this.platformConfig.storeHash}/${this.platformConfig.apiVersion}`;
    this.headers = {
      'X-Auth-Token': this.platformConfig.accessToken,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    if (this.platformConfig.clientId) {
      this.headers['X-Auth-Client'] = this.platformConfig.clientId;
    }
  }

  async connect(): Promise<ConnectionResult> {
    try {
      const response = await this.makeRequest('GET', '/store');
      if (!response.ok) {
        const error = `Failed to connect to BigCommerce: ${response.status} ${response.statusText}`;
        await this.emitEvent('connection.failed', { 
          platform: 'bigcommerce', 
          error 
        });
        return {
          success: false,
          message: error
        };
      }
      
      await this.emitEvent('connection.established', { platform: 'bigcommerce' });
      return {
        success: true,
        message: 'Successfully connected to BigCommerce store'
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      await this.emitEvent('connection.failed', { 
        platform: 'bigcommerce', 
        error: errorMessage 
      });
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  async disconnect(): Promise<void> {
    await this.emitEvent('connection.closed', { platform: 'bigcommerce' });
  }

  async testConnection(): Promise<TestResult> {
    const startTime = Date.now();
    try {
      const response = await this.makeRequest('GET', '/store');
      const duration = Date.now() - startTime;
      
      if (response.ok) {
        return {
          success: true,
          message: 'Connection test successful',
          duration
        };
      } else {
        return {
          success: false,
          message: `Connection test failed: ${response.status} ${response.statusText}`,
          duration
        };
      }
    } catch (error) {
      const duration = Date.now() - startTime;
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Connection test failed',
        duration
      };
    }
  }

  async validateConfig(): Promise<ValidationResult> {
    const errors: Array<{ field: string; message: string; code: string }> = [];
    const warnings: Array<{ field: string; message: string; suggestion?: string }> = [];

    if (!this.platformConfig.storeHash) {
      errors.push({ field: 'storeHash', message: 'Store hash is required', code: 'REQUIRED' });
    } else if (!/^[a-z0-9]{10}$/.test(this.platformConfig.storeHash)) {
      errors.push({ field: 'storeHash', message: 'Store hash must be a 10-character alphanumeric string', code: 'INVALID_FORMAT' });
    }

    if (!this.platformConfig.accessToken) {
      errors.push({ field: 'accessToken', message: 'Access token is required', code: 'REQUIRED' });
    } else if (this.platformConfig.accessToken.length < 20) {
      errors.push({ field: 'accessToken', message: 'Access token appears to be invalid (too short)', code: 'INVALID_LENGTH' });
    }

    if (!this.platformConfig.webhookSecret) {
      warnings.push({ 
        field: 'webhookSecret', 
        message: 'Webhook secret not provided - real-time sync will be disabled',
        suggestion: 'Add a webhook secret to enable real-time synchronization'
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  async syncProducts(): Promise<EcommerceProduct[]> {
    try {
      const products: EcommerceProduct[] = [];
      let page = 1;
      const limit = 100;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await this.makeRequest('GET', `/catalog/products?page=${page}&limit=${limit}&include=images,variants`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch products: ${response.status}`);
        }

        const data = await response.json();
        const bigCommerceProducts: BigCommerceProduct[] = data.data || [];

        for (const bcProduct of bigCommerceProducts) {
          const product = await this.convertBigCommerceProductToEcommerce(bcProduct);
          products.push(product);
        }

        hasMorePages = data.meta?.pagination?.current_page < data.meta?.pagination?.total_pages;
        page++;
      }

      await this.emitEvent('products.synced', { count: products.length });
      return products;
    } catch (error) {
      await this.emitEvent('products.sync_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  async syncCustomers(): Promise<EcommerceCustomer[]> {
    try {
      const customers: EcommerceCustomer[] = [];
      let page = 1;
      const limit = 100;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await this.makeRequest('GET', `/customers?page=${page}&limit=${limit}&include=addresses`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch customers: ${response.status}`);
        }

        const data = await response.json();
        const bigCommerceCustomers: BigCommerceCustomer[] = data.data || [];

        for (const bcCustomer of bigCommerceCustomers) {
          const customer = await this.convertBigCommerceCustomerToEcommerce(bcCustomer);
          customers.push(customer);
        }

        hasMorePages = data.meta?.pagination?.current_page < data.meta?.pagination?.total_pages;
        page++;
      }

      await this.emitEvent('customers.synced', { count: customers.length });
      return customers;
    } catch (error) {
      await this.emitEvent('customers.sync_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  async syncOrders(dateRange?: { start: Date; end: Date }): Promise<EcommerceOrder[]> {
    try {
      const orders: EcommerceOrder[] = [];
      let page = 1;
      const limit = 100;
      let hasMorePages = true;

      while (hasMorePages) {
        let url = `/orders?page=${page}&limit=${limit}&include=products,shipping_addresses`;
        
        if (dateRange) {
          const startDate = dateRange.start.toISOString().split('T')[0];
          const endDate = dateRange.end.toISOString().split('T')[0];
          url += `&min_date_created=${startDate}&max_date_created=${endDate}`;
        }

        const response = await this.makeRequest('GET', url);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch orders: ${response.status}`);
        }

        const data = await response.json();
        const bigCommerceOrders: BigCommerceOrder[] = data.data || [];

        for (const bcOrder of bigCommerceOrders) {
          const order = await this.convertBigCommerceOrderToEcommerce(bcOrder);
          orders.push(order);
        }

        hasMorePages = data.meta?.pagination?.current_page < data.meta?.pagination?.total_pages;
        page++;
      }

      await this.emitEvent('orders.synced', { count: orders.length, dateRange });
      return orders;
    } catch (error) {
      await this.emitEvent('orders.sync_failed', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        dateRange 
      });
      throw error;
    }
  }

  async getProduct(productId: string): Promise<EcommerceProduct | null> {
    try {
      const response = await this.makeRequest('GET', `/catalog/products/${productId}?include=images,variants`);
      
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch product: ${response.status}`);
      }

      const data = await response.json();
      const bigCommerceProduct: BigCommerceProduct = data.data;
      return await this.convertBigCommerceProductToEcommerce(bigCommerceProduct);
    } catch (error) {
      await this.emitEvent('product.fetch_failed', { 
        productId, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  async getCustomer(customerId: string): Promise<EcommerceCustomer | null> {
    try {
      const response = await this.makeRequest('GET', `/customers/${customerId}?include=addresses`);
      
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch customer: ${response.status}`);
      }

      const data = await response.json();
      const bigCommerceCustomer: BigCommerceCustomer = data.data;
      return await this.convertBigCommerceCustomerToEcommerce(bigCommerceCustomer);
    } catch (error) {
      await this.emitEvent('customer.fetch_failed', { 
        customerId, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  async getOrder(orderId: string): Promise<EcommerceOrder | null> {
    try {
      const response = await this.makeRequest('GET', `/orders/${orderId}?include=products,shipping_addresses`);
      
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Failed to fetch order: ${response.status}`);
      }

      const data = await response.json();
      const bigCommerceOrder: BigCommerceOrder = data.data;
      return await this.convertBigCommerceOrderToEcommerce(bigCommerceOrder);
    } catch (error) {
      await this.emitEvent('order.fetch_failed', { 
        orderId, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  async updateInventory(updates: { productId: string; variantId?: string; inventory: number }[]): Promise<void> {
    try {
      for (const update of updates) {
        if (update.variantId) {
          const variantUpdate = {
            inventory_level: update.inventory
          };

          const response = await this.makeRequest(
            'PUT', 
            `/catalog/products/${update.productId}/variants/${update.variantId}`,
            variantUpdate
          );

          if (!response.ok) {
            throw new Error(`Failed to update variant inventory: ${response.status}`);
          }
        } else {
          const productUpdate = {
            inventory_level: update.inventory,
            inventory_tracking: update.inventory > 0 ? 'product' : 'none'
          };

          const response = await this.makeRequest(
            'PUT', 
            `/catalog/products/${update.productId}`,
            productUpdate
          );

          if (!response.ok) {
            throw new Error(`Failed to update product inventory: ${response.status}`);
          }
        }
      }

      await this.emitEvent('inventory.updated', { updates });
    } catch (error) {
      await this.emitEvent('inventory.update_failed', { 
        updates, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  async getRecommendations(context: RecommendationContext): Promise<EcommerceProduct[]> {
    try {
      const recommendations: EcommerceProduct[] = [];

      if (context.type === 'trending') {
        const response = await this.makeRequest('GET', `/catalog/products?sort=total_sold&direction=desc&limit=${context.limit}&include=images`);
        
        if (response.ok) {
          const data = await response.json();
          const bigCommerceProducts: BigCommerceProduct[] = data.data || [];

          for (const bcProduct of bigCommerceProducts) {
            const product = await this.convertBigCommerceProductToEcommerce(bcProduct);
            recommendations.push(product);
          }
        }
      } else if (context.category) {
        const response = await this.makeRequest('GET', `/catalog/products?categories:in=${context.category}&limit=${context.limit}&include=images`);
        
        if (response.ok) {
          const data = await response.json();
          const bigCommerceProducts: BigCommerceProduct[] = data.data || [];

          for (const bcProduct of bigCommerceProducts) {
            const product = await this.convertBigCommerceProductToEcommerce(bcProduct);
            recommendations.push(product);
          }
        }
      }

      await this.emitEvent('recommendations.generated', { 
        context, 
        count: recommendations.length 
      });
      
      return recommendations;
    } catch (error) {
      await this.emitEvent('recommendations.generation_failed', { 
        context, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      throw error;
    }
  }

  // Helper methods

  private async makeRequest(method: string, endpoint: string, body?: any): Promise<Response> {
    const url = `${this.baseUrl}${endpoint}`;
    const options: RequestInit = {
      method,
      headers: this.headers
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    return fetch(url, options);
  }

  private async convertBigCommerceProductToEcommerce(bcProduct: BigCommerceProduct): Promise<EcommerceProduct> {
    const images: ProductImage[] = [];
    
    if (bcProduct.images) {
      bcProduct.images.forEach((img, index) => {
        images.push({
          id: img.id?.toString() || index.toString(),
          url: img.url_standard || img.image_url || '',
          altText: img.description || bcProduct.name,
          position: img.sort_order || index
        });
      });
    }

    const variants: ProductVariant[] = [];
    
    if (bcProduct.variants) {
      bcProduct.variants.forEach(variant => {
        const options: Record<string, string> = {};
        
        if (variant.option_values) {
          variant.option_values.forEach(optionValue => {
            options[optionValue.option_display_name] = optionValue.label;
          });
        }

        variants.push({
          id: variant.id?.toString() || '',
          title: `${bcProduct.name} - ${Object.values(options).join(', ')}`,
          price: variant.price || bcProduct.price,
          sku: variant.sku || bcProduct.sku || '',
          inventory: variant.inventory_level || 0,
          options
        });
      });
    }

    return {
      id: bcProduct.id.toString(),
      title: bcProduct.name,
      description: bcProduct.description || '',
      price: bcProduct.price,
      compareAtPrice: bcProduct.retail_price,
      currency: 'USD',
      sku: bcProduct.sku,
      inventory: bcProduct.inventory_level || 0,
      images,
      categories: bcProduct.categories.map(cat => cat.toString()),
      tags: bcProduct.search_keywords ? bcProduct.search_keywords.split(',').map(tag => tag.trim()) : [],
      variants,
      status: bcProduct.is_visible ? 'active' : 'draft',
      createdAt: bcProduct.date_created ? new Date(bcProduct.date_created) : new Date(),
      updatedAt: bcProduct.date_modified ? new Date(bcProduct.date_modified) : new Date(),
      customFields: {
        bigCommerceId: bcProduct.id,
        type: bcProduct.type,
        retailPrice: bcProduct.retail_price,
        isVisible: bcProduct.is_visible
      }
    };
  }

  private async convertBigCommerceCustomerToEcommerce(bcCustomer: BigCommerceCustomer): Promise<EcommerceCustomer> {
    const addresses: CustomerAddress[] = [];
    
    if (bcCustomer.addresses) {
      bcCustomer.addresses.forEach(addr => {
        addresses.push({
          id: addr.id?.toString() || '',
          firstName: addr.first_name,
          lastName: addr.last_name,
          company: addr.company,
          address1: addr.address1,
          address2: addr.address2,
          city: addr.city,
          province: addr.state_or_province,
          country: addr.country_code,
          zip: addr.postal_code,
          phone: addr.phone,
          isDefault: false
        });
      });
    }

    return {
      id: bcCustomer.id?.toString() || '',
      email: bcCustomer.email,
      firstName: bcCustomer.first_name,
      lastName: bcCustomer.last_name,
      phone: bcCustomer.phone,
      addresses,
      totalSpent: 0, // Would need to calculate from orders
      orderCount: 0, // Would need to calculate from orders
      averageOrderValue: 0, // Would need to calculate from orders
      tags: [],
      acceptsMarketing: true, // Default value, BigCommerce doesn't have this field
      createdAt: bcCustomer.date_created ? new Date(bcCustomer.date_created) : new Date(),
      updatedAt: bcCustomer.date_modified ? new Date(bcCustomer.date_modified) : new Date(),
      customFields: {
        company: bcCustomer.company,
        storeCredit: bcCustomer.store_credit,
        customerGroupId: bcCustomer.customer_group_id,
        notes: bcCustomer.notes
      }
    };
  }

  private async convertBigCommerceOrderToEcommerce(bcOrder: BigCommerceOrder): Promise<EcommerceOrder> {
    const lineItems: OrderLineItem[] = [];
    
    if (bcOrder.products) {
      bcOrder.products.forEach(product => {
        lineItems.push({
          id: product.id?.toString() || '',
          productId: product.product_id.toString(),
          variantId: undefined,
          title: product.name,
          quantity: product.quantity,
          price: product.price_inc_tax,
          totalPrice: product.total_inc_tax,
          sku: product.sku
        });
      });
    }

    let billingAddress: CustomerAddress | undefined;
    if (bcOrder.billing_address) {
      billingAddress = {
        id: '0',
        firstName: bcOrder.billing_address.first_name,
        lastName: bcOrder.billing_address.last_name,
        company: bcOrder.billing_address.company,
        address1: bcOrder.billing_address.street_1 || '',
        address2: bcOrder.billing_address.street_2,
        city: bcOrder.billing_address.city || '',
        province: bcOrder.billing_address.state,
        country: bcOrder.billing_address.country_iso2 || '',
        zip: bcOrder.billing_address.zip || '',
        phone: bcOrder.billing_address.phone,
        isDefault: false
      };
    }

    let shippingAddress: CustomerAddress | undefined;
    if (bcOrder.shipping_addresses && bcOrder.shipping_addresses.length > 0) {
      const addr = bcOrder.shipping_addresses[0];
      shippingAddress = {
        id: addr.id?.toString() || '0',
        firstName: addr.first_name,
        lastName: addr.last_name,
        company: addr.company,
        address1: addr.street_1 || '',
        address2: addr.street_2,
        city: addr.city || '',
        province: addr.state,
        country: addr.country_iso2 || '',
        zip: addr.zip || '',
        phone: addr.phone,
        isDefault: false
      };
    }

    let status: 'pending' | 'paid' | 'fulfilled' | 'cancelled' | 'refunded' = 'pending';
    switch (bcOrder.status_id) {
      case 1:
        status = 'pending';
        break;
      case 2:
      case 3:
      case 10:
        status = 'fulfilled';
        break;
      case 5:
        status = 'cancelled';
        break;
      case 4:
        status = 'refunded';
        break;
      default:
        status = 'pending';
    }

    return {
      id: bcOrder.id?.toString() || '',
      orderNumber: bcOrder.id?.toString() || '',
      customerId: bcOrder.customer_id?.toString() || '',
      email: bcOrder.billing_address?.email || '',
      totalPrice: bcOrder.total_inc_tax,
      subtotalPrice: bcOrder.subtotal_inc_tax,
      taxPrice: bcOrder.total_tax,
      shippingPrice: bcOrder.shipping_cost_inc_tax,
      currency: bcOrder.currency_code,
      status,
      lineItems,
      shippingAddress,
      billingAddress,
      createdAt: bcOrder.date_created ? new Date(bcOrder.date_created) : new Date(),
      updatedAt: bcOrder.date_modified ? new Date(bcOrder.date_modified) : new Date(),
      customFields: {
        bigCommerceStatus: bcOrder.status,
        statusId: bcOrder.status_id,
        paymentMethod: bcOrder.payment_method,
        paymentStatus: bcOrder.payment_status,
        isDigital: bcOrder.order_is_digital,
        channelId: bcOrder.channel_id,
        orderSource: bcOrder.order_source
      }
    };
  }
}